import { type Ref, useEffect, useImperativeHandle } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'

import { useReportingI18n } from '../i18n'
import { downloadCsv, getExportTimestamp } from '../utils/csvExport'

export interface DashboardExportHandle {
  exportCsv: () => void
}

interface UseExportCsvOptions {
  ref: Ref<DashboardExportHandle>
  csvRows: Record<string, unknown>[]
  filenamePrefix: string
  isLoading: boolean
  hasError: boolean
  toast?: ToastLike
  onExportAvailabilityChange?: (canExport: boolean) => void
}

export function useExportCsv({
  ref,
  csvRows,
  filenamePrefix,
  isLoading,
  hasError,
  toast,
  onExportAvailabilityChange,
}: UseExportCsvOptions) {
  const { text } = useReportingI18n()

  const canExportCsv = !isLoading && !hasError && csvRows.length > 0

  useEffect(() => {
    onExportAvailabilityChange?.(canExportCsv)
  }, [canExportCsv, onExportAvailabilityChange])

  useImperativeHandle(
    ref,
    () => ({
      exportCsv: () => {
        if (!canExportCsv) return

        const result = downloadCsv(
          csvRows,
          `${filenamePrefix}_${getExportTimestamp()}`,
        )

        if (result.success) {
          toast?.create({
            title: text('dashboards.csvExport.success', 'CSV exported'),
            type: 'success',
          })
          return
        }

        toast?.create({
          title: text('dashboards.csvExport.failed', 'CSV export failed'),
          description: result.error,
          type: 'error',
        })
      },
    }),
    [canExportCsv, csvRows, filenamePrefix, text, toast],
  )

  return { canExportCsv }
}
